import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Search, X, Send, Users, GraduationCap, MapPin, Layers, Info, Loader2, Ban, Phone } from "lucide-react";
import { toast } from "sonner";
import TemplatePicker from "@/components/TemplatePicker";
import AttachToolbar, { AttachmentPreview } from "@/components/AttachToolbar";
import SegmentMeter from "@/components/SegmentMeter";
import { familyLabel } from "@/lib/utils";

// Build a student-first display label. Prefer student name(s) tied to this contact,
// fall back to parent's own name only if no students are on file.
function studentLabel(c) {
  const names = (c?.students || []).map((s) => (s?.name || s?.display_name || "").trim()).filter(Boolean);
  if (names.length > 0) return familyLabel(names);
  if (c?.student_display) return c.student_display;
  const parent = `${c?.first_name || ""} ${c?.last_name || ""}`.trim();
  return parent || c?.phone || "Unknown";
}

function parentSubLabel(c) {
  const parent = `${c?.first_name || ""} ${c?.last_name || ""}`.trim();
  return parent ? `Parent: ${parent}` : "";
}

export default function ComposeModal({ open, onOpenChange, onSent, initialBody = "", initialPhone = "", initialMediaUrls = [] }) {
  const [tab, setTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [classes, setClasses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [q, setQ] = useState("");
  const [classQ, setClassQ] = useState("");
  const [picked, setPicked] = useState({}); // {contact_id: contact}
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [shortenLinks, setShortenLinks] = useState(true);
  const [pickedClasses, setPickedClasses] = useState({}); // {class_id: {name, count}}
  const [addingBulk, setAddingBulk] = useState(false);
  const [dayFilter, setDayFilter] = useState("All");
  const [newPhone, setNewPhone] = useState("");
  const [newFirst, setNewFirst] = useState("");
  const [newLast, setNewLast] = useState("");
  const [addingNumber, setAddingNumber] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [{ data: c }, { data: cl }, { data: loc }] = await Promise.all([
          api.get("/contacts"),
          api.get("/classes"),
          api.get("/locations"),
        ]);
        setContacts(c || []);
        setClasses(cl || []);
        setLocations(loc || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setPicked({}); setBody(""); setQ(""); setClassQ(""); setTab("contacts"); setAttachments([]); setPickedClasses({}); setDayFilter("All"); setNewPhone(""); setNewFirst(""); setNewLast("");
      return;
    }
    // Pre-fill body/phone/media when caller passes them in (used by "Forward" from Inbox).
    // Deps intentionally only include `open` — initial* props are read once at open-time.
    // (Including them caused an infinite render loop when parents passed inline `[]` defaults.)
    if (initialBody) setBody(initialBody);
    if (initialPhone) { setTab("new-number"); setNewPhone(initialPhone); }
    if (initialMediaUrls && initialMediaUrls.length) {
      setAttachments(initialMediaUrls.map((url, i) => ({
        id: `fwd-${i}-${Date.now()}`,
        url,
        content_type: "image/jpeg",
      })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filteredContacts = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return contacts;
    return contacts.filter((c) => {
      const students = (c.students || []).map((st) => (st?.name || "").toLowerCase()).join(" ");
      return (
        students.includes(s) ||
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(s) ||
        (c.phone || "").includes(s) ||
        (c.email || "").toLowerCase().includes(s)
      );
    });
  }, [contacts, q]);

  const togglePick = (c) => {
    setPicked((prev) => {
      const next = { ...prev };
      if (next[c.id]) delete next[c.id];
      else next[c.id] = c;
      return next;
    });
  };

  const clearPicked = () => setPicked({});

  const addByClass = async (classId, className) => {
    try {
      const { data } = await api.get(`/classes/${classId}/students`);
      const add = {};
      (data || []).forEach((s) => {
        // Merge multiple students on same phone into a single pill (e.g. siblings)
        const existing = add[s.contact_id];
        const students = existing ? [...(existing.students || []), { name: s.student_name }] : [{ name: s.student_name }];
        add[s.contact_id] = {
          id: s.contact_id,
          first_name: (s.contact_name || "").split(" ")[0] || "",
          last_name: (s.contact_name || "").split(" ").slice(1).join(" "),
          phone: s.phone,
          email: s.email,
          students,
          student_display: familyLabel(students.map((x) => x.name)),
        };
      });
      const count = Object.keys(add).length;
      setPicked((prev) => ({ ...prev, ...add }));
      toast.success(`Added ${count} student${count === 1 ? "" : "s"} from ${className}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleClassPick = (cls) => {
    setPickedClasses((prev) => {
      const next = { ...prev };
      if (next[cls.id]) delete next[cls.id];
      else next[cls.id] = { name: cls.name, count: cls.enrolled_count || 0 };
      return next;
    });
  };

  const addFromSelectedClasses = async () => {
    const ids = Object.keys(pickedClasses);
    if (ids.length === 0) return;
    setAddingBulk(true);
    try {
      const results = await Promise.all(ids.map((id) => api.get(`/classes/${id}/students`).then((r) => r.data)));
      const add = {};
      results.forEach((list) => {
        (list || []).forEach((s) => {
          const existing = add[s.contact_id];
          const priorNames = new Set((existing?.students || []).map((x) => x.name));
          const students = existing ? [...existing.students] : [];
          if (s.student_name && !priorNames.has(s.student_name)) students.push({ name: s.student_name });
          add[s.contact_id] = {
            id: s.contact_id,
            first_name: (s.contact_name || "").split(" ")[0] || "",
            last_name: (s.contact_name || "").split(" ").slice(1).join(" "),
            phone: s.phone,
            email: s.email,
            students,
            student_display: familyLabel(students.map((x) => x.name)),
          };
        });
      });
      const count = Object.keys(add).length;
      setPicked((prev) => ({ ...prev, ...add }));
      setPickedClasses({});
      toast.success(`Added ${count} student${count === 1 ? "" : "s"} from ${ids.length} class${ids.length === 1 ? "" : "es"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAddingBulk(false); }
  };

  const addByLocation = async (code, label) => {
    try {
      const { data } = await api.get(`/locations/${code}/contacts`);
      const add = {};
      (data || []).forEach((c) => { add[c.id] = c; });
      const count = Object.keys(add).length;
      setPicked((prev) => ({ ...prev, ...add }));
      toast.success(`Added ${count} recipient${count === 1 ? "" : "s"} from ${label}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const activeStaff = contacts.filter((c) => (c.tags || []).includes("staff") && !(c.tags || []).includes("staff_inactive"));
  const addStaff = () => {
    const add = {};
    activeStaff.forEach((c) => { add[c.id] = c; });
    setPicked((prev) => ({ ...prev, ...add }));
    toast.success(`Added ${activeStaff.length} active staff`);
  };

  const addAll = () => {
    const add = {};
    contacts.forEach((c) => { add[c.id] = c; });
    setPicked(add);
    toast.success(`Added all ${contacts.length} contacts`);
  };

  const addByNewNumber = async () => {
    const digits = (newPhone || "").replace(/\D/g, "");
    if (digits.length < 10) { toast.error("Enter a 10-digit phone number"); return; }
    setAddingNumber(true);
    try {
      const { data } = await api.post("/contacts/find-or-create-by-phone", {
        phone: newPhone.trim(),
        first_name: newFirst.trim(),
        last_name: newLast.trim(),
      });
      // Add to picked with a friendly display so the pill shows the number/name
      const displayName = `${data.first_name || ""} ${data.last_name || ""}`.trim();
      setPicked((prev) => ({
        ...prev,
        [data.id]: {
          ...data,
          student_display: displayName && displayName !== "New Contact" ? displayName : data.phone,
        },
      }));
      // Prepend to contacts list so it's visible in the Students tab search too
      setContacts((prev) => (prev.some((c) => c.id === data.id) ? prev : [data, ...prev]));
      setNewPhone(""); setNewFirst(""); setNewLast("");
      toast.success(data.created ? `Added ${data.phone} — new contact` : `Added existing contact ${displayName || data.phone}`);
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't add that number"));
    } finally {
      setAddingNumber(false);
    }
  };

  const pickedList = Object.values(picked);
  const [pickedQ, setPickedQ] = useState("");
  const optedOutInSelection = pickedList.filter((c) => (c.tags || []).includes("opted_out"));
  const willSendCount = pickedList.length - optedOutInSelection.length;

  const doSend = async () => {
    if (!body.trim() && attachments.length === 0) return toast.error("Type a message or attach an image");
    if (pickedList.length === 0) return toast.error("Pick at least one recipient");
    setSending(true);
    try {
      const { data } = await api.post("/messages/compose-send", {
        contact_ids: pickedList.map((c) => c.id),
        body,
        media_urls: attachments.map((a) => a.url),
        shorten_links: shortenLinks,
      });
      const total = data.total_recipients || pickedList.length;
      toast.success(`Broadcast queued — ${total} text${total === 1 ? "" : "s"} sending in the background. See progress in Broadcasts.`);
      onSent && onSent();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const insertEmoji = (emoji) => {
    const el = textareaRef.current;
    if (!el) { setBody((b) => b + emoji); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + emoji + body.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 max-h-[92vh] flex flex-col overflow-y-auto overflow-x-hidden overscroll-contain" data-testid="compose-modal">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
          <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Send className="w-5 h-5 text-[#D9232E]" /> Send Text
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-sm">
            Pick one person or many. Every recipient gets their <span className="font-semibold text-slate-700">own individual text</span> — replies come back as separate conversations, never a group thread.
          </DialogDescription>
        </DialogHeader>

        {/* Selected recipients bar */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              Recipients <span className="text-[#D9232E]">({pickedList.length})</span>
            </div>
            {pickedList.length > 0 && (
              <button
                type="button"
                onClick={clearPicked}
                className="text-xs text-slate-500 hover:text-[#D9232E]"
                data-testid="compose-clear-recipients"
              >
                Clear all
              </button>
            )}
          </div>
          {pickedList.length === 0 ? (
            <div className="text-xs text-slate-400 italic">No one selected yet. Use the tabs below to add recipients.</div>
          ) : (
            <>
            {pickedList.length > 12 && (
              <div className="relative mb-2">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={pickedQ} onChange={(e) => setPickedQ(e.target.value)} placeholder="Find someone in this list to remove..." className="h-8 pl-8 text-xs bg-white" data-testid="compose-recipient-search" />
              </div>
            )}
            <div className="max-h-40 overflow-y-auto overscroll-contain thin-scroll pr-1" data-testid="compose-recipient-scroll">
              <div className="flex flex-wrap gap-1.5" data-testid="compose-recipient-pills">
                {pickedList.filter((c) => !pickedQ.trim() || `${studentLabel(c)} ${parentSubLabel(c)} ${c.phone || ""}`.toLowerCase().includes(pickedQ.trim().toLowerCase())).map((c) => (
                  <Badge key={c.id} variant="secondary" className="pl-2 pr-1 py-1 bg-white border border-slate-200 text-slate-700 gap-1" title={parentSubLabel(c)}>
                    <span className="text-xs">{studentLabel(c)}</span>
                    {(c.tags || []).includes("opted_out") && (
                      <Ban className="w-3 h-3 text-amber-500" title="Opted out" />
                    )}
                    <button type="button" onClick={() => togglePick(c)} className="ml-0.5 hover:text-[#D9232E]" data-testid={`compose-remove-${c.id}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
            </>
          )}
        </div>

        {/* Picker tabs */}
        <Tabs value={tab} onValueChange={setTab} className="shrink-0 flex flex-col">
          <TabsList className="mx-6 mt-3 bg-slate-100 rounded-md w-fit max-w-[calc(100%-3rem)] overflow-x-auto justify-start thin-scroll">
            <TabsTrigger value="contacts" data-testid="compose-tab-contacts"><GraduationCap className="w-3.5 h-3.5 mr-1.5" />Students</TabsTrigger>
            <TabsTrigger value="new-number" data-testid="compose-tab-new-number"><Phone className="w-3.5 h-3.5 mr-1.5" />New #</TabsTrigger>
            <TabsTrigger value="classes" data-testid="compose-tab-classes"><GraduationCap className="w-3.5 h-3.5 mr-1.5" />Class</TabsTrigger>
            <TabsTrigger value="locations" data-testid="compose-tab-locations"><MapPin className="w-3.5 h-3.5 mr-1.5" />Location</TabsTrigger>
            <TabsTrigger value="staff" data-testid="compose-tab-staff"><Users className="w-3.5 h-3.5 mr-1.5" />Staff</TabsTrigger>
            <TabsTrigger value="everyone" data-testid="compose-tab-everyone"><Layers className="w-3.5 h-3.5 mr-1.5" />Everyone</TabsTrigger>
          </TabsList>

          <div className="h-[340px] sm:h-[380px] overflow-hidden px-6 py-3 flex flex-col">
            <TabsContent value="contacts" className="mt-0 flex-1 min-h-0 flex flex-col data-[state=inactive]:hidden">
              <div className="relative mb-2">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Search by student name, parent, phone, or email..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-10 bg-slate-50 border-slate-200" data-testid="compose-contact-search" />
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain thin-scroll border border-slate-200 rounded-md bg-white" data-testid="compose-contact-list">
                {filteredContacts.length === 0 ? (
                  <div className="p-8 text-center text-sm text-slate-400">No contacts match.</div>
                ) : filteredContacts.map((c) => {
                  const isPicked = !!picked[c.id];
                  const optedOut = (c.tags || []).includes("opted_out");
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => togglePick(c)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 border-b border-slate-100 text-left transition-colors ${isPicked ? "bg-red-50" : "hover:bg-slate-50"}`}
                      data-testid={`compose-contact-${c.id}`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isPicked ? "bg-[#D9232E] border-[#D9232E]" : "border-slate-300"}`}>
                        {isPicked && <span className="text-white text-[10px] leading-none">✓</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
                          {studentLabel(c)}
                          {optedOut && <Ban className="w-3.5 h-3.5 text-amber-500" title="Opted out — will be skipped" />}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {parentSubLabel(c) && <span>{parentSubLabel(c)} · </span>}
                          {c.phone}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="classes" className="mt-0 flex-1 min-h-0 flex flex-col data-[state=inactive]:hidden">
              <div className="relative mb-2">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search classes by name, day, or instructor..."
                  value={classQ}
                  onChange={(e) => setClassQ(e.target.value)}
                  className="pl-9 h-10 bg-slate-50 border-slate-200"
                  data-testid="compose-class-search"
                />
              </div>
              <div className="flex items-center gap-1 mb-2 flex-wrap">
                <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mr-1">Day:</span>
                {["All","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDayFilter(d)}
                    className={`px-2 py-0.5 rounded text-[11px] font-semibold border transition-colors ${dayFilter === d ? "bg-[#D9232E] text-white border-[#D9232E]" : "bg-white text-slate-600 border-slate-200 hover:border-[#D9232E] hover:text-[#D9232E]"}`}
                    data-testid={`compose-day-${d.toLowerCase()}`}
                  >
                    {d === "All" ? "All" : d.slice(0,3)}
                  </button>
                ))}
                <div className="flex-1" />
                {(() => {
                  const s = classQ.trim().toLowerCase();
                  const filtered = classes.filter((c) => {
                    if (dayFilter !== "All" && (c.day || "") !== dayFilter) return false;
                    if (s) {
                      const hay = `${c.name || ""} ${c.day || ""} ${c.instructor || ""} ${c.location_code || ""}`.toLowerCase();
                      if (!hay.includes(s)) return false;
                    }
                    return true;
                  });
                  return filtered.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const next = { ...pickedClasses };
                        filtered.forEach((c) => { next[c.id] = { name: c.name, count: c.enrolled_count || 0 }; });
                        setPickedClasses(next);
                      }}
                      className="text-[11px] font-semibold text-[#D9232E] hover:underline"
                      data-testid="compose-select-all-visible"
                    >
                      Select all {filtered.length} visible
                    </button>
                  );
                })()}
              </div>
              {Object.keys(pickedClasses).length > 0 && (
                <div className="mb-2 flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200">
                  <div className="text-xs text-slate-700">
                    <span className="font-bold text-[#D9232E]">{Object.keys(pickedClasses).length}</span> class{Object.keys(pickedClasses).length === 1 ? "" : "es"} selected
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setPickedClasses({})} className="text-xs text-slate-500 hover:text-[#D9232E]" data-testid="compose-clear-classes">
                      Clear
                    </button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={addFromSelectedClasses}
                      disabled={addingBulk}
                      className="h-7 bg-[#D9232E] hover:bg-[#B91C25] text-white text-xs"
                      data-testid="compose-add-selected-classes"
                    >
                      {addingBulk ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                      Add students from selected
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain thin-scroll border border-slate-200 rounded-md bg-white" data-testid="compose-class-list">
                {(() => {
                  const s = classQ.trim().toLowerCase();
                  const filtered = classes.filter((c) => {
                    if (dayFilter !== "All" && (c.day || "") !== dayFilter) return false;
                    if (s) {
                      const hay = `${c.name || ""} ${c.day || ""} ${c.instructor || ""} ${c.location_code || ""} ${c.division || ""}`.toLowerCase();
                      if (!hay.includes(s)) return false;
                    }
                    return true;
                  });
                  if (filtered.length === 0) {
                    return <div className="p-8 text-center text-sm text-slate-400">No classes match your filters.</div>;
                  }
                  return filtered.map((cl) => {
                    const isPicked = !!pickedClasses[cl.id];
                    return (
                      <button
                        key={cl.id}
                        type="button"
                        onClick={() => toggleClassPick(cl)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 border-b border-slate-100 text-left transition-colors ${isPicked ? "bg-red-50" : "hover:bg-slate-50"}`}
                        data-testid={`compose-class-row-${cl.id}`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isPicked ? "bg-[#D9232E] border-[#D9232E]" : "border-slate-300"}`}>
                          {isPicked && <span className="text-white text-[10px] leading-none">✓</span>}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-slate-900 truncate">{cl.name}</div>
                          <div className="text-xs text-slate-500 truncate">{cl.day} · {cl.time} · {cl.instructor || "TBA"}</div>
                        </div>
                        <Badge variant="secondary" className="text-[10px] shrink-0">
                          <Users className="w-3 h-3 mr-1" /> {cl.enrolled_count || 0}
                        </Badge>
                      </button>
                    );
                  });
                })()}
              </div>
              <div className="mt-2 text-[11px] text-slate-500 flex items-start gap-1.5">
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>Filter by day + tap classes to select multiple. Sibling phones are only added once — the text goes to each family's phone.</span>
              </div>
            </TabsContent>

            <TabsContent value="locations" className="mt-0 flex-1 min-h-0 flex flex-col data-[state=inactive]:hidden">
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain thin-scroll border border-slate-200 rounded-md bg-white" data-testid="compose-location-list">
                {locations.length === 0 ? (
                  <div className="p-8 text-center text-sm text-slate-400">No locations found.</div>
                ) : locations.map((l) => (
                  <div key={l.code} className="flex items-center justify-between gap-3 px-3 py-2.5 border-b border-slate-100">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900">{l.label}</div>
                      <div className="text-xs text-slate-500">{l.class_count} classes · {l.student_count} families</div>
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={() => addByLocation(l.code, l.label)} data-testid={`compose-add-location-${l.code}`}>
                      Add everyone
                    </Button>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="staff" className="mt-0 flex-1 min-h-0 flex flex-col data-[state=inactive]:hidden">
              <div className="flex-1 border border-slate-200 rounded-md bg-white p-6 flex flex-col items-center justify-center gap-3 text-center">
                <Users className="w-8 h-8 text-slate-400" />
                <div className="text-sm text-slate-700 font-semibold">Active staff group</div>
                <div className="text-xs text-slate-500 max-w-sm">{activeStaff.length} active staff. Each gets their own text. Manage the list on the Staff Group page.</div>
                <Button type="button" onClick={addStaff} disabled={activeStaff.length === 0} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="compose-add-staff">
                  Add {activeStaff.length} active staff
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="everyone" className="mt-0 flex-1 min-h-0 flex flex-col data-[state=inactive]:hidden">
              <div className="flex-1 border border-slate-200 rounded-md bg-white p-6 flex flex-col items-center justify-center gap-3 text-center">
                <Layers className="w-8 h-8 text-slate-400" />
                <div className="text-sm text-slate-700 font-semibold">Everyone in your contacts</div>
                <div className="text-xs text-slate-500 max-w-sm">Adds every parent, staff, and known contact ({contacts.length} people). Great for studio-wide announcements. STOPped contacts are always skipped.</div>
                <Button type="button" onClick={addAll} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="compose-add-everyone">
                  Add all {contacts.length}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="new-number" className="mt-0 flex-1 min-h-0 overflow-y-auto data-[state=inactive]:hidden">
              <div className="h-full border border-slate-200 rounded-md bg-white p-6 flex flex-col gap-4">
                <div className="flex items-start gap-2">
                  <Phone className="w-5 h-5 text-[#D9232E] shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Text any number</div>
                    <div className="text-xs text-slate-500 mt-0.5">Type a 10-digit phone number and send them a text. If they aren't in your contacts yet, we'll add them so replies land in your inbox.</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-1">
                    <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Phone number*</label>
                    <Input
                      type="tel"
                      inputMode="tel"
                      autoComplete="off"
                      placeholder="(361) 555-1234"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addByNewNumber(); } }}
                      className="mt-1 h-10"
                      data-testid="compose-new-number-phone"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">First name (optional)</label>
                    <Input
                      placeholder="First"
                      value={newFirst}
                      onChange={(e) => setNewFirst(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addByNewNumber(); } }}
                      className="mt-1 h-10"
                      data-testid="compose-new-number-first"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Last name (optional)</label>
                    <Input
                      placeholder="Last"
                      value={newLast}
                      onChange={(e) => setNewLast(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addByNewNumber(); } }}
                      className="mt-1 h-10"
                      data-testid="compose-new-number-last"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] text-slate-500 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>Type the phone, hit Add — it drops into your recipients above. Then type your message below and hit Send.</span>
                  </div>
                  <Button
                    type="button"
                    onClick={addByNewNumber}
                    disabled={addingNumber || (newPhone || "").replace(/\D/g, "").length < 10}
                    className="bg-[#D9232E] hover:bg-[#B91C25] text-white font-semibold"
                    data-testid="compose-new-number-add"
                  >
                    {addingNumber ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Phone className="w-4 h-4 mr-1.5" />}
                    Add to recipients
                  </Button>
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>

        {/* Message body */}
        <div className="border-t border-slate-200 px-6 py-4 bg-white">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3 pb-2 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Add:</span>
              <AttachToolbar
                onEmoji={insertEmoji}
                onAttach={(m) => setAttachments((a) => [...a, m])}
                testIdPrefix="compose-attach"
              />
              <TemplatePicker onSelect={(b) => setBody(b)} testId="compose-template-picker" />
              <label className="flex items-center gap-1.5 pl-2 border-l border-slate-200 cursor-pointer text-xs text-slate-600 select-none" title="Shortens long links (via TinyURL) so texts use fewer characters">
                <input type="checkbox" checked={shortenLinks} onChange={(e) => setShortenLinks(e.target.checked)} className="accent-[#D9232E]" data-testid="compose-shorten-toggle" />
                Shorten links
              </label>
            </div>
            <span className="text-[11px] text-slate-400">
              <SegmentMeter body={body} recipients={Object.keys(picked).length || 1} variant="broadcast" onFix={setBody} testId="compose-segment-meter" />
            </span>
          </div>
          <AttachmentPreview items={attachments} onRemove={(id) => setAttachments((a) => a.filter((x) => x.id !== id))} testIdPrefix="compose-attach" />
          <Textarea
            ref={textareaRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type your message... it will send as an individual text to each recipient."
            className="min-h-[90px] resize-none border-slate-200"
            data-testid="compose-message-input"
          />
          {optedOutInSelection.length > 0 && (
            <div className="mt-2 flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{optedOutInSelection.length} recipient{optedOutInSelection.length === 1 ? " has" : "s have"} texted STOP and will be skipped.</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-xs text-slate-600">
            Will send <span className="font-bold text-slate-900">{willSendCount}</span> individual text{willSendCount === 1 ? "" : "s"}.
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="compose-cancel">Cancel</Button>
            <Button
              onClick={doSend}
              disabled={sending || willSendCount === 0 || (!body.trim() && attachments.length === 0)}
              className="bg-[#D9232E] hover:bg-[#B91C25] text-white font-semibold"
              data-testid="compose-send"
            >
              {sending ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-1.5" /> Send to {willSendCount}</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
